import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  CheckCircle, AlertTriangle, XCircle, ExternalLink,
  Loader2, RefreshCw, ChevronDown, ChevronUp, Globe
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const StatusIcon = ({ status }) => {
  if (status === 'ok') return <CheckCircle className="w-4 h-4 text-green-400" />;
  if (status === 'warning') return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
  return <XCircle className="w-4 h-4 text-red-400" />;
};

const StatusBadge = ({ status }) => {
  const colors = { ok: 'bg-green-900/50 text-green-300', warning: 'bg-yellow-900/50 text-yellow-300', error: 'bg-red-900/50 text-red-300' };
  const labels = { ok: 'OK', warning: 'Advarsel', error: 'Feil' };
  return <Badge className={colors[status] || colors.error}>{labels[status] || 'Feil'}</Badge>;
};

const AdminSchemaTest = () => {
  const { user, token } = useAuth();
  const [pages, setPages] = useState([]);
  const [results, setResults] = useState({});
  const [testing, setTesting] = useState({});
  const [expanded, setExpanded] = useState({});
  const [testingAll, setTestingAll] = useState(false);

  useEffect(() => {
    if (token) {
      fetchPages();
    }
  }, [token]);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/schema-test/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(res.data);
    } catch (err) {
      console.error('Failed to fetch pages:', err);
    }
  };

  const testPage = async (path) => {
    setTesting(prev => ({ ...prev, [path]: true }));
    try {
      const fullUrl = `${BACKEND_URL}${path}`;
      const res = await axios.post(`${API}/admin/schema-test`, { url: fullUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(prev => ({ ...prev, [path]: res.data }));
    } catch (err) {
      setResults(prev => ({ ...prev, [path]: { page_error: err.message, schemas: [] } }));
    }
    setTesting(prev => ({ ...prev, [path]: false }));
  };

  const testAllPages = async () => {
    setTestingAll(true);
    for (const page of pages) {
      await testPage(page.path);
    }
    setTestingAll(false);
  };

  const getOverallStatus = (result) => {
    if (!result || result.page_error) return 'error';
    if (result.errors > 0) return 'error';
    if (result.warnings > 0) return 'warning';
    if (result.total_schemas > 0) return 'ok';
    return 'error';
  };

  const testedCount = Object.keys(results).length;
  const okCount = Object.values(results).filter(r => getOverallStatus(r) === 'ok').length;
  const warnCount = Object.values(results).filter(r => getOverallStatus(r) === 'warning').length;
  const errCount = Object.values(results).filter(r => getOverallStatus(r) === 'error').length;

  return (
    <div className="space-y-6" data-testid="schema-test-panel">
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-400" />
              Schema Markup Validator
            </CardTitle>
            <Button
              onClick={testAllPages}
              disabled={testingAll}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="test-all-schemas-btn"
            >
              {testingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {testingAll ? 'Tester...' : 'Test alle sider'}
            </Button>
          </div>
          <p className="text-gray-400 text-sm mt-1">
            Validerer JSON-LD strukturerte data mot schema.org-krav. Tester at bedriftsinformasjon er korrekt i schema markup.
          </p>
        </CardHeader>
        <CardContent>
          {testedCount > 0 && (
            <div className="flex gap-4 mb-6 p-3 bg-gray-900/50 rounded-lg">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-green-300 text-sm font-medium">{okCount} OK</span>
              </div>
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-300 text-sm font-medium">{warnCount} Advarsler</span>
              </div>
              <div className="flex items-center gap-1.5">
                <XCircle className="w-4 h-4 text-red-400" />
                <span className="text-red-300 text-sm font-medium">{errCount} Feil</span>
              </div>
              <span className="text-gray-500 text-sm ml-auto">{testedCount}/{pages.length} testet</span>
            </div>
          )}

          <div className="space-y-2">
            {pages.map((page) => {
              const result = results[page.path];
              const isLoading = testing[page.path];
              const isExpanded = expanded[page.path];
              const status = result ? getOverallStatus(result) : null;

              return (
                <div key={page.path} className="border border-gray-700 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between p-3 bg-gray-800/30">
                    <div className="flex items-center gap-3">
                      {status && <StatusIcon status={status} />}
                      <div>
                        <span className="text-white text-sm font-medium">{page.title}</span>
                        <span className="text-gray-500 text-xs ml-2">{page.path}</span>
                      </div>
                      {result && (
                        <Badge className="bg-gray-700 text-gray-300 text-xs">
                          {result.total_schemas || 0} schema{result.total_schemas !== 1 ? 'er' : ''}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {result?.google_test_url && (
                        <a
                          href={result.google_test_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 p-1"
                          title="Åpne i Google Rich Results Test"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-gray-600 text-gray-300 h-8"
                        onClick={() => testPage(page.path)}
                        disabled={isLoading}
                        data-testid={`test-schema-${page.path.replace(/\//g, '-')}`}
                      >
                        {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test'}
                      </Button>
                      {result && (
                        <button
                          onClick={() => setExpanded(prev => ({ ...prev, [page.path]: !isExpanded }))}
                          className="text-gray-400 hover:text-white p-1"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && result && (
                    <div className="p-3 bg-gray-900/50 border-t border-gray-700 space-y-3">
                      {result.page_error && (
                        <div className="text-red-400 text-sm p-2 bg-red-900/20 rounded">
                          Sidefeil: {result.page_error}
                        </div>
                      )}
                      {result.schemas?.map((schema, idx) => (
                        <div key={idx} className="p-3 bg-gray-800/50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <StatusBadge status={schema.status} />
                            <span className="text-white text-sm font-mono">@type: {schema.type}</span>
                            <span className="text-gray-500 text-xs">{schema.fields_found} felt</span>
                          </div>
                          {schema.issues?.length > 0 && (
                            <div className="space-y-1 mt-2">
                              {schema.issues.map((issue, i) => (
                                <div key={i} className="text-red-400 text-xs flex items-center gap-1.5">
                                  <XCircle className="w-3 h-3 flex-shrink-0" /> {issue}
                                </div>
                              ))}
                            </div>
                          )}
                          {schema.warnings?.length > 0 && (
                            <div className="space-y-1 mt-2">
                              {schema.warnings.map((warn, i) => (
                                <div key={i} className="text-yellow-400 text-xs flex items-center gap-1.5">
                                  <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {warn}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSchemaTest;
